import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useUserSubscription, useGrantSubscription, usePlans } from '@/hooks/useSubscription';
import { formatDate } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { AssinaturaStatus } from '@/types/subscription.types';
import { PlanoSection } from './subscriptionModal/PlanoSection';
import { LimitesSection } from './subscriptionModal/LimitesSection';
import { FinanceiroSection } from './subscriptionModal/FinanceiroSection';
import { SummaryBanner } from './subscriptionModal/SummaryBanner';
import { useSubscriptionModalForm } from './subscriptionModal/useSubscriptionModalForm';

interface Props {
  userId: number;
  userName: string;
  onClose: () => void;
}

const STATUS_LABEL: Record<AssinaturaStatus, string> = {
  trial: 'Trial',
  active: 'Ativa',
  comp: 'Cortesia',
  past_due: 'Pendente',
  canceled: 'Cancelada',
  expired: 'Expirada',
};

export function ManageSubscriptionModal(props: Props) {
  const { data: subData, isLoading: carregandoAssinatura } = useUserSubscription(props.userId);
  const { data: plans, isLoading: carregandoPlanos } = usePlans();
  const isLoading = carregandoAssinatura || carregandoPlanos;

  // O formulário guarda o plano, o ciclo e o valor ao montar. Só monta depois
  // que a assinatura e os planos chegaram; antes disso abria em "Teste grátis"
  // mesmo para quem já tem Enterprise.
  return (
    <ConteudoAssinatura
      key={isLoading ? 'carregando' : `assinatura-${subData?.assinatura?.id ?? 'nova'}`}
      {...props}
      subData={subData}
      plans={plans}
      isLoading={isLoading}
    />
  );
}

interface ConteudoProps extends Props {
  subData: ReturnType<typeof useUserSubscription>['data'];
  plans: ReturnType<typeof usePlans>['data'];
  isLoading: boolean;
}

function ConteudoAssinatura({
  userId,
  userName,
  onClose,
  subData,
  plans,
  isLoading,
}: ConteudoProps) {
  const grantMutation = useGrantSubscription();

  const assinatura = subData?.assinatura ?? null;
  const entitlement = subData?.entitlement;
  const uso = subData?.uso;
  const isSaving = grantMutation.isPending;

  const form = useSubscriptionModalForm(assinatura, plans);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const isEnterprise = form.planoCard === 'enterprise';

  // Escape fecha + trava scroll do body (mesmo shell do EditUserDrawer).
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, isSaving]);

  useEffect(() => {
    setFeedback(null);
  }, [form.planoCard]);

  const handleError = (err: unknown) => {
    const fallback = 'Erro ao aplicar a assinatura. Verifique os dados e tente novamente.';
    let msg = fallback;
    if (typeof err === 'object' && err !== null) {
      const maybe = err as { response?: { data?: { message?: string; error?: string } } };
      msg = maybe.response?.data?.message ?? maybe.response?.data?.error ?? fallback;
    }
    setFeedback({ type: 'error', msg });
  };

  const handleApply = async () => {
    const pagoSemValor =
      (form.planoCard === 'basico' || form.planoCard === 'profissional') &&
      !form.cortesia &&
      form.valorPagoNumero == null;
    if (pagoSemValor) {
      setFeedback({ type: 'error', msg: 'Informe o valor pago, ou ligue a cortesia.' });
      return;
    }
    const payload = form.buildPayload();
    try {
      await grantMutation.mutateAsync({ userId, data: payload });
      setFeedback({
        type: 'success',
        msg: isEnterprise ? 'Contrato Enterprise aplicado.' : 'Assinatura aplicada.',
      });
    } catch (err) {
      console.error('Erro ao aplicar assinatura:', err);
      handleError(err);
    }
  };

  // Subtítulo do header: "<plano atual> · expira <data> · <uso>/<limite> animais · <uso>/<limite> propriedades".
  // Contrato sem plano do catálogo é Enterprise (o entitlement devolve o plano
  // usado como base dos limites, que não é o que foi contratado).
  const ehEnterprise =
    assinatura != null && assinatura.plano_id == null && assinatura.status !== 'trial';
  const atualLabel = entitlement
    ? `${ehEnterprise ? 'Enterprise' : (entitlement.plano?.nome ?? STATUS_LABEL[entitlement.status])}${
        entitlement.validade.current_period_end
          ? ` · expira ${formatDate(entitlement.validade.current_period_end)}`
          : ''
      } · ${uso?.animais ?? 0}/${entitlement.limites.animais || '∞'} animais · ${
        uso?.propriedades ?? 0
      }/${entitlement.limites.propriedades || '∞'} propriedades`
    : userName;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Assinatura"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm dark:bg-black/70"
        onClick={() => !isSaving && onClose()}
      />

      {/* Drawer — coluna única (igual ao protótipo). Acento azul (padrão) / verde (Enterprise). */}
      <div
        className={cn(
          'relative z-10 flex h-full w-full max-w-md animate-slide-in-right flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] border-l bg-bg-elevated shadow-2xl ring-1 transition-colors',
          isEnterprise
            ? 'border-tertiary/40 ring-tertiary/10'
            : 'border-border ring-black/5 dark:ring-white/10'
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-text-title">
              Assinatura — {userName}
            </h2>
            <p className="mt-0.5 text-xs text-text-secondary">{atualLabel}</p>
          </div>
          <button
            onClick={() => !isSaving && onClose()}
            disabled={isSaving}
            aria-label="Fechar"
            className="shrink-0 rounded-lg p-1 text-text-secondary transition-colors hover:bg-muted hover:text-text-title disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        {isLoading ? (
          <div className="flex-1 space-y-6 overflow-y-auto p-6">
            <Skeleton className="h-5 w-32 rounded-full" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-14 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
              <Skeleton className="h-14 rounded-xl" />
            </div>
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : (
          <div className="flex-1 space-y-6 overflow-y-auto p-6">
            {feedback && (
              <div
                role="status"
                className={cn(
                  'flex items-start gap-2 rounded-lg border px-4 py-3 text-sm',
                  feedback.type === 'success'
                    ? 'border-success/30 bg-success/10 text-success'
                    : 'border-error/30 bg-error/10 text-error'
                )}
              >
                {feedback.type === 'success' ? (
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle size={18} className="mt-0.5 shrink-0" />
                )}
                <span>{feedback.msg}</span>
              </div>
            )}

            {/* ① Plano e ciclo/vigência */}
            <PlanoSection form={form} disabled={isSaving} />

            {/* ② Limites */}
            <LimitesSection
              form={form}
              planoNome={form.planoSelecionado?.nome ?? null}
              disabled={isSaving}
            />

            {/* ③ Financeiro */}
            <FinanceiroSection form={form} disabled={isSaving} />

            {!isEnterprise && (
              <p className="text-xs text-text-secondary">
                Precisa de limites ou valores diferentes? Use o plano{' '}
                <button
                  type="button"
                  onClick={() => form.setPlanoCard('enterprise')}
                  className="font-medium text-tertiary underline-offset-2 hover:underline"
                >
                  Enterprise
                </button>
                .
              </p>
            )}

            {/* Resumo */}
            <SummaryBanner form={form} atualLabel={atualLabel} />
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={handleApply}
            disabled={isSaving || isLoading}
            className={
              isEnterprise
                ? 'bg-tertiary text-on-tertiary shadow-cta hover:bg-tertiary/90'
                : undefined
            }
          >
            {isSaving && <RefreshCw size={16} className="animate-spin" />}
            {isSaving ? 'Aplicando...' : isEnterprise ? 'Aplicar contrato' : 'Aplicar assinatura'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
